import React, { useState } from 'react';
import {
  Box, Button, Typography, TextField, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Alert,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { toast } from 'react-toastify';
import { generateCopyText, parseImportText, FORMAT_HEADER } from '../../utils/jobFormCopyPaste';

const JobFormCopyPasteTools = ({
  formData = {},
  salaryMonthly,
  jobTimingStart,
  jobTimingEnd,
  companyOptions = [],
  onPopulate,
}) => {
  const [pasteModalOpen, setPasteModalOpen]     = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [pastedText, setPastedText]             = useState('');
  const [parsedResult, setParsedResult]         = useState(null);

  const handleCopyForm = () => {
    const copyText = generateCopyText({
      formData,
      salaryMonthly,
      jobTimingStart,
      jobTimingEnd,
    });
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(copyText)
        .then(() => toast.success('Job details copied successfully.'))
        .catch(() => fallbackCopy(copyText));
    } else {
      fallbackCopy(copyText);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Job details copied successfully.');
    } catch (err) {
      toast.error('Failed to copy job details to clipboard.');
    }
  };

  const handleParsePastedText = () => {
    if (!pastedText.trim()) {
      toast.error('Please paste client response text first.');
      return;
    }
    const result = parseImportText(pastedText, companyOptions, {
      ...formData,
      salaryMonthly,
      jobTimingStart,
      jobTimingEnd,
    });

    if (!result.hasFields) {
      toast.warning('No valid job requirement fields recognized in pasted text.');
      return;
    }

    setParsedResult(result);
    setPasteModalOpen(false);
    setPreviewModalOpen(true);
  };

  const handleConfirmPopulate = () => {
    if (!parsedResult || !parsedResult.parsedData) return;
    if (onPopulate) {
      onPopulate(parsedResult.parsedData);
    }
    setPreviewModalOpen(false);
    setPastedText('');
    setParsedResult(null);
    toast.success('Form populated from imported data. Please review before saving.');
  };

  return (
    <>
      {/* Action Toolbar */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        mb: 2.5, p: 1.5, bgcolor: '#f8f9ff', borderRadius: '10px', border: '1px solid #e8eaf6',
        flexWrap: 'wrap', gap: 1, width: '100%',
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" fontWeight={700} color="#3f51b5">
            📋 Requirement Data Tools:
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Copy form text for client or paste client's response to import fields
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyForm}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, borderColor: '#3f51b5', color: '#3f51b5' }}
          >
            Copy
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<ContentPasteIcon />}
            onClick={() => setPasteModalOpen(true)}
            sx={{
              borderRadius: '8px', textTransform: 'none', fontWeight: 600,
              background: 'linear-gradient(135deg, #3f51b5, #5c6bc0)',
            }}
          >
            Paste / Import
          </Button>
        </Box>
      </Box>

      {/* ── PASTE INPUT MODAL ── */}
      <Dialog
        open={pasteModalOpen}
        onClose={() => setPasteModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#3f51b5">
              Paste Client Response Text
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Paste the text returned by the client. Fields will be extracted and parsed automatically.
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setPasteModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <TextField
            multiline
            rows={12}
            fullWidth
            placeholder={`Paste raw text here... e.g.:\n${FORMAT_HEADER}\nJob Title: Senior Developer\nLocation: Mumbai\nOpenings: 5\n...`}
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setPasteModalOpen(false)}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleParsePastedText}
            sx={{
              borderRadius: '8px', textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #3f51b5, #5c6bc0)',
            }}
          >
            Parse & Preview
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── PREVIEW & CONFIRM MODAL ── */}
      <Dialog
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#3f51b5">
              Import Preview & Field Comparison
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Review changes below before populating the form. Missing fields are kept unchanged.
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setPreviewModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {parsedResult?.warnings && parsedResult.warnings.length > 0 && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ borderRadius: '10px' }}>
              <Typography variant="subtitle2" fontWeight={700}>Warnings Detected:</Typography>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {parsedResult.warnings.map((w, idx) => (
                  <li key={idx}>
                    <Typography variant="caption" fontWeight={600}>{w}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px', maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f6ff', color: '#3f51b5' } }}>
                  <TableCell>Field</TableCell>
                  <TableCell>Current Form Value</TableCell>
                  <TableCell>Imported Value</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedResult?.diff.map((row) => (
                  <TableRow key={row.key} sx={{
                    bgcolor: row.status === 'changed' ? '#f0f4ff' : 'inherit',
                    '&:hover': { bgcolor: '#f8f9ff' }
                  }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{row.label}</TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', color: row.currentVal ? 'text.primary' : 'text.secondary' }}>
                      {row.currentVal || '—'}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '0.82rem',
                      fontWeight: row.status === 'changed' ? 700 : 400,
                      color: row.status === 'changed' ? '#2e7d32' : 'text.primary'
                    }}>
                      {row.importedVal}
                    </TableCell>
                    <TableCell align="center">
                      {row.status === 'changed' && (
                        <Chip label="Changed" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
                      )}
                      {row.status === 'unchanged' && (
                        <Chip label="Unchanged" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                      {row.status === 'missing' && (
                        <Chip label="Missing (Kept)" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setPreviewModalOpen(false);
              setPasteModalOpen(true);
            }}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            Back to Edit Paste
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmPopulate}
            sx={{
              borderRadius: '8px', textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #2e7d32, #4caf50)',
              '&:hover': { background: 'linear-gradient(135deg, #1b5e20, #2e7d32)' },
            }}
          >
            Confirm & Populate Form
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JobFormCopyPasteTools;
